import { useEffect, useState } from "react";
import { Box, Stack, Typography, IconButton, Paper } from "@mui/material";
import { CheckCircle, Pencil, Check, X } from "lucide-react";
import PipelineMultiSelector from "./PipelineMultiSelector";
import PipelineStageEditor from "./PipelineStageEditor";
import { useSettings } from "../../../context/SettingsContext";
import PageTitle from "../../../components/PageTitle";

// Pre-Consultation Pipeline Statuses
const PRE_CONSULT_STATUSES = [
  "qualified",
  "need_analysis",
  "booked",
  "dropped",
];

export default function PreConsultPipelineStatus() {
  const { pipeline_status, savePipeline } = useSettings();

  const [editing, setEditing] = useState(false);
  const [stages, setStages] = useState([]);
  const [savedStages, setSavedStages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Filter pipeline_status to get only pre-consult statuses
    const preConsultStages = (pipeline_status || []).filter((status) =>
      PRE_CONSULT_STATUSES.includes(status)
    );
    setStages(preConsultStages);
    setSavedStages(preConsultStages);
  }, [pipeline_status]);

  const save = async () => {
    try {
      setSaving(true);
      // Merge pre-consult stages with existing pipeline statuses
      const otherPipelineStages = (pipeline_status || []).filter(
        (status) => !PRE_CONSULT_STATUSES.includes(status)
      );
      const updatedPipelineStatus = [...otherPipelineStages, ...stages];
      await savePipeline(updatedPipelineStatus);
      setSavedStages(stages);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setStages(savedStages);
    setEditing(false);
  };

  return (
    <Box elevation={0}>
      <Stack direction="row" justifyContent="space-between" mb={4}>
        <PageTitle parts={["Settings", "Pre-Consult Pipeline", "Stages"]} />

        {!editing ? (
          <IconButton
            onClick={() => setEditing(true)}
            sx={{
              bgcolor: "#059669",
              color: "white",
              borderRadius: 999,
              px: 2,
              py: 1,
              "&:hover": {
                bgcolor: "#047857",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(5,150,105,0.45)",
              },
            }}
          >
            <Pencil size={16} />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <IconButton
              disabled={saving}
              onClick={save}
              sx={{
                bgcolor: "#16A34A",
                color: "white",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "#15803D",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(22,163,74,0.45)",
                },
              }}
            >
              <Check size={16} />
            </IconButton>

            <IconButton
              disabled={saving}
              onClick={cancel}
              sx={{
                bgcolor: "rgba(220,38,38,0.15)",
                color: "#DC2626",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "rgba(220,38,38,0.25)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(220,38,38,0.35)",
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        <PipelineMultiSelector
          title="Pre-Consult Pipeline Stages"
          icon={CheckCircle}
          selected={stages}
          setSelected={setStages}
          editing={editing}
          filterStatuses={PRE_CONSULT_STATUSES}
        />

        <PipelineStageEditor
          flow={stages}
          setFlow={setStages}
          editing={editing}
        />
      </Box>
    </Box>
  );
}
